import { makeAutoObservable, runInAction } from "mobx";
import ApiStore from "./ApiStore";
import {
  Category,
  CategoryDto,
  ClientRole,
  Contact,
  ContactDto,
  ContactType,
  CrmAttachment,
  CrmChangeLog,
  CrmEntityType,
  CrmEvent,
  CrmEventDto,
  CrmEventType,
  CrmReportExportRequest,
  CrmReportRequest,
  CrmReportResult,
  CrmUploadResult,
  ExternalCompany,
  ExternalCompanyDto,
  Region,
  RegionDto
} from "../api/CRMApiClient";

import { GoogleSyncToastState } from "../components/crm/GoogleSyncToast";

class CRMStore {
  isLoading = false;
  error: string | null = null;

  syncToast: GoogleSyncToastState = {
    visible: false,
    status: 'syncing',
  };

  setSyncToast = (toast: GoogleSyncToastState | ((prev: GoogleSyncToastState) => GoogleSyncToastState)) => {
    if (typeof toast === 'function') {
      this.syncToast = toast(this.syncToast);
    } else {
      this.syncToast = toast;
    }
  };

  closeSyncToast = () => {
    this.syncToast = { ...this.syncToast, visible: false };
  };

  contacts: Contact[] = [];
  clientRoles: ClientRole[] = [];
  contactTypes: ContactType[] = [];
  CrmEventTypes: CrmEventType[] = [];
  categories: Category[] = [];
  regions: Region[] = [];
  isRegionsLoaded = false;

  get regionsMap(): Map<number, Region> {
    const map = new Map<number, Region>();
    for (const r of this.regions) map.set(r.id, r);
    return map;
  }

  externalCompanies: ExternalCompany[] = [];
  CrmEvents: CrmEvent[] = [];

  currentContact: Contact | null = null;
  currentCrmEvent: CrmEvent | null = null;

  isCategoriesLoaded = false;
  isContactTypesLoaded = false;
  isCrmEventTypesLoaded = false;
  isClientRolesLoaded = false;

  get categoriesMap(): Map<number, Category> {
    const map = new Map<number, Category>();
    for (const c of this.categories) {
      map.set(c.id, c);
    }
    return map;
  }

  get clientRolesMap(): Map<number, ClientRole> {
    const map = new Map<number, ClientRole>();
    for (const r of this.clientRoles) {
      map.set(r.id, r);
    }
    return map;
  }

  get contactTypesMap(): Map<number, ContactType> {
    const map = new Map<number, ContactType>();
    for (const t of this.contactTypes) {
      map.set(t.id, t);
    }
    return map;
  }

  public readonly ApiStore: ApiStore;
  private readonly api: ApiStore;

  isGoogleSyncing = false;

  constructor(apiStore: ApiStore) {
    makeAutoObservable(this);
    this.api = apiStore;
    this.ApiStore = apiStore;

    try {
      const syncTimestamp = sessionStorage.getItem('crm_google_contacts_syncing');
      if (syncTimestamp) {
        const elapsed = Date.now() - parseInt(syncTimestamp, 10);
        if (elapsed < 120000) {
          this.isGoogleSyncing = true;
          setTimeout(() => {
            runInAction(() => {
              this.isGoogleSyncing = false;
              sessionStorage.removeItem('crm_google_contacts_syncing');
            });
          }, Math.max(1000, 120000 - elapsed));
        } else {
          sessionStorage.removeItem('crm_google_contacts_syncing');
        }
      }
    } catch {}
  }

  setGoogleSyncing = (syncing: boolean) => {
    runInAction(() => {
      this.isGoogleSyncing = syncing;
      try {
        if (syncing) {
          sessionStorage.setItem('crm_google_contacts_syncing', Date.now().toString());
        } else {
          sessionStorage.removeItem('crm_google_contacts_syncing');
        }
      } catch {}
    });
  };

  private setLoading = (v: boolean) => { this.isLoading = v; };
  private setError = (e: any) => { this.error = String(e ?? ""); };

  // UPDATED: Enhanced contact methods with performance optimization
  getContacts = async (includeDetails = false) => {
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getContacts(includeDetails);
      runInAction(() => { this.contacts = list; this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  // NEW: Fast method for list views (no related data)
  getContactsFast = async () => {
    return this.getContacts(false);
  };

  // NEW: Detailed method for when you need related data
  getContactsWithDetails = async () => {
    return this.getContacts(true);
  };

  getContactsByExternalCompany = async (externalCompanyId: number) => {
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getContactsByExternalCompany(externalCompanyId);
      runInAction(() => { this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  getContactById = async (id: number) => {
    this.setLoading(true);
    try {
      const c = await this.api.CRMApiClient.getContactById(id);
      runInAction(() => { this.currentContact = c; this.setLoading(false); });
      return c;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  addContact = async (dto: ContactDto) => {
    this.setLoading(true);
    this.setError(null);
    try {
      const created = await this.api.CRMApiClient.createContact(dto);
      if (created) {
        runInAction(() => {
          this.contacts.push(created);
        });
      }
      runInAction(() => { this.setLoading(false); });
      return created;
    } catch (err: any) {
      runInAction(() => { this.setError(err?.message || err); this.setLoading(false); });
      return null;
    }
  };

  updateContact = async (id: number, dto: ContactDto) => {
    this.setLoading(true);
    this.setError(null);
    try {
      const ok = await this.api.CRMApiClient.updateContact(id, dto);
      if (ok) {
        const updated = await this.getContactById(id);
        runInAction(() => {
          if (updated) {
            const idx = this.contacts.findIndex(c => c.id === id);
            if (idx !== -1) {
              this.contacts[idx] = updated;
            }
          }
        });
      }
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err: any) {
      runInAction(() => { this.setError(err?.message || err); this.setLoading(false); });
      return false;
    }
  };

  deleteContact = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteContact(id);
      if (ok) runInAction(() => { this.contacts = this.contacts.filter(c => c.id !== id); });
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  connectGoogleContacts = async (code: string, redirectUri?: string): Promise<{ connected?: boolean; email?: string; enableGoogleContactsSync?: boolean; error?: string }> => {
    try {
      return await this.api.CRMApiClient.connectGoogleContacts(code, redirectUri);
    } catch (err: any) {
      return { error: err?.message || 'Failed to connect Google account' };
    }
  };

  disconnectGoogleContacts = async (): Promise<{ disconnected?: boolean; error?: string }> => {
    try {
      return await this.api.CRMApiClient.disconnectGoogleContacts();
    } catch (err: any) {
      return { error: err?.message || 'Failed to disconnect Google account' };
    }
  };
  syncGoogleContacts = async (accessToken?: string) => {
    this.setGoogleSyncing(true);
    try {
      const res = await this.api.CRMApiClient.syncGoogleContacts(accessToken);
      return res;
    } catch (err) {
      runInAction(() => { this.setError(err); });
      return null;
    } finally {
      this.setGoogleSyncing(false);
    }
  };

  syncSingleContactToGoogle = async (id: number, accessToken?: string): Promise<{ resourceName?: string; error?: string }> => {
    try {
      return await this.api.CRMApiClient.syncSingleContactToGoogle(id, accessToken);
    } catch (err: any) {
      return { error: err?.message || 'Failed to sync contact' };
    }
  };

  getClientRoles = async (force = false) => {
    if (this.isClientRolesLoaded && !force && this.clientRoles.length > 0) {
      return this.clientRoles;
    }
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getClientRoles();
      runInAction(() => {
        this.clientRoles = list;
        this.isClientRolesLoaded = true;
        this.setLoading(false);
      });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  addClientRole = async (dto: { name: string }) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createClientRole(dto);
      if (created) {
        await this.getClientRoles(true);
      } else {
        runInAction(() => { this.setLoading(false); });
      }
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateClientRole = async (id: number, dto: { name: string }) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateClientRole(id, dto);
      if (ok) await this.getClientRoles(true);
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteClientRole = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteClientRole(id);
      if (ok) await this.getClientRoles(true);
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getContactTypes = async (force = false) => {
    if (this.isContactTypesLoaded && !force && this.contactTypes.length > 0) {
      return this.contactTypes;
    }
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getContactTypes();
      runInAction(() => {
        this.contactTypes = list;
        this.isContactTypesLoaded = true;
        this.setLoading(false);
      });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  addContactType = async (dto: { name: string }) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createContactType(dto);
      if (created) {
        await this.getContactTypes(true);
      } else {
        runInAction(() => { this.setLoading(false); });
      }
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateContactType = async (id: number, dto: { name: string }) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateContactType(id, dto);
      if (ok) await this.getContactTypes(true);
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteContactType = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteContactType(id);
      if (ok) await this.getContactTypes(true);
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getCrmEventTypes = async (force = false) => {
    if (this.isCrmEventTypesLoaded && !force && this.CrmEventTypes.length > 0) {
      return this.CrmEventTypes;
    }
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getCrmEventTypes();
      runInAction(() => {
        this.CrmEventTypes = list;
        this.isCrmEventTypesLoaded = true;
        this.setLoading(false);
      });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  addCrmEventType = async (dto: { name: string }) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createCrmEventType(dto);
      if (created) {
        await this.getCrmEventTypes(true);
      } else {
        runInAction(() => { this.setLoading(false); });
      }
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateCrmEventType = async (id: number, dto: { name: string }) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateCrmEventType(id, dto);
      if (ok) await this.getCrmEventTypes(true);
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteCrmEventType = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteCrmEventType(id);
      if (ok) await this.getCrmEventTypes(true);
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getCategories = async (force = false) => {
    if (this.isCategoriesLoaded && !force && this.categories.length > 0) {
      return this.categories;
    }
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getCategories();
      runInAction(() => {
        this.categories = list;
        this.isCategoriesLoaded = true;
        this.setLoading(false);
      });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  addCategory = async (dto: CategoryDto) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createCategory(dto);
      if (created) {
        await this.getCategories(true);
      } else {
        runInAction(() => { this.setLoading(false); });
      }
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateCategory = async (id: number, dto: CategoryDto) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateCategory(id, dto);
      if (ok) await this.getCategories(true);
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteCategory = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteCategory(id);
      if (ok) await this.getCategories(true);
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getRegions = async (force = false) => {
    if (this.isRegionsLoaded && !force && this.regions.length > 0) return this.regions;
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getRegions();
      runInAction(() => { this.regions = list; this.isRegionsLoaded = true; this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  addRegion = async (dto: RegionDto) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createRegion(dto);
      if (created) await this.getRegions(true); else runInAction(() => { this.setLoading(false); });
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateRegion = async (id: number, dto: RegionDto) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateRegion(id, dto);
      if (ok) await this.getRegions(true);
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteRegion = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteRegion(id);
      if (ok) await this.getRegions(true);
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getExternalCompanies = async () => {
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getExternalCompanies();
      runInAction(() => { this.externalCompanies = list; this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  addExternalCompany = async (dto: ExternalCompanyDto) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createExternalCompany(dto);
      runInAction(() => {
        if (created) this.externalCompanies.push(created);
        this.setLoading(false);
      });
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateExternalCompany = async (id: number, dto: ExternalCompanyDto) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateExternalCompany(id, dto);
      if (ok) await this.getExternalCompanies();
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteExternalCompany = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteExternalCompany(id);
      if (ok) runInAction(() => { this.externalCompanies = this.externalCompanies.filter(c => c.id !== id); });
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getCrmEvents = async (includeDetails = false) => {
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getCrmEvents(includeDetails);
      runInAction(() => { this.CrmEvents = list; this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  getCrmEventsFast = async () => {
    return this.getCrmEvents(false);
  };

  getCrmEventsWithDetails = async () => {
    return this.getCrmEvents(true);
  };

  getCrmEventById = async (id: number) => {
    this.setLoading(true);
    try {
      const ev = await this.api.CRMApiClient.getCrmEventById(id);
      runInAction(() => { this.currentCrmEvent = ev; this.setLoading(false); });
      return ev;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  addCrmEvent = async (dto: CrmEventDto) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createCrmEvent(dto);
      runInAction(() => {
        if (created) this.CrmEvents.push(created);
        this.setLoading(false);
      });
      return created?.id;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return undefined;
    }
  };

  updateCrmEvent = async (id: number, dto: CrmEventDto) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateCrmEvent(id, dto);
      if (ok) await this.getCrmEvents(); // Refresh the list
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteCrmEvent = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteCrmEvent(id);
      if (ok) runInAction(() => { this.CrmEvents = this.CrmEvents.filter(e => e.id !== id); });
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  /** History is fetched on demand and not cached: it is per-record and small. */
  getCrmHistory = async (entityType: CrmEntityType, entityId: number): Promise<CrmChangeLog[]> => {
    try {
      return await this.api.CRMApiClient.getCrmHistory(entityType, entityId);
    } catch (err) {
      runInAction(() => { this.setError(err); });
      return [];
    }
  };

  getCrmAttachments = async (entityType: CrmEntityType, entityId: number): Promise<CrmAttachment[]> => {
    try {
      return await this.api.CRMApiClient.getCrmAttachments(entityType, entityId);
    } catch (err) {
      runInAction(() => { this.setError(err); });
      return [];
    }
  };

  uploadCrmAttachment = async (entityType: CrmEntityType, entityId: number, file: File): Promise<CrmUploadResult> =>
    this.api.CRMApiClient.uploadCrmAttachment(entityType, entityId, file);

  deleteCrmAttachment = async (id: number): Promise<boolean> => {
    try {
      return await this.api.CRMApiClient.deleteCrmAttachment(id);
    } catch (err) {
      runInAction(() => { this.setError(err); });
      return false;
    }
  };

  loadAllData = async () => {
    this.setLoading(true);
    try {
      await Promise.all([
        this.getContactsFast(),      // Fast loading for lists
        this.getClientRoles(),
        this.getContactTypes(),
        this.getCrmEventTypes(),
        this.getExternalCompanies(),
        this.getCrmEventsFast(),     // Fast loading for lists
      ]);
      runInAction(() => { this.setLoading(false); });
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
    }
  };

  loadDetailedData = async () => {
    this.setLoading(true);
    try {
      await Promise.all([
        this.getContactsWithDetails(), // Detailed loading with related data
        this.getClientRoles(),
        this.getContactTypes(),
        this.getCrmEventTypes(),
        this.getExternalCompanies(),
        this.getCrmEventsWithDetails(), // Detailed loading with related data
      ]);
      runInAction(() => { this.setLoading(false); });
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
    }
  };

  parseAddress = (text: string) => this.ApiStore.CRMApiClient.parseAddress(text);

  aggregateCrmReport = async (request: CrmReportRequest): Promise<CrmReportResult | null> =>
    this.api.CRMApiClient.aggregateCrmReport(request);

  exportCrmReport = async (request: CrmReportExportRequest, format: 'xlsx' | 'pdf') =>
    this.api.CRMApiClient.exportCrmReport(request, format);
}

export default CRMStore;